import {
    Dispatch,
    ReactElement,
    ReactNode,
    SetStateAction,
    createContext,
    useMemo,
    useState,
} from "react"

import useSubmitDraft from "../../../services/middleware/hooks/draftApplication/useSubmitDraft"
import {
    IFormData,
    ISupplementingInfo,
} from "../../../services/middleware/types/amendApplication/launchAmendmentTypes"
import {
    IApplicationForms,
    IApplicationSpec,
    IFormTitlesIndexMap,
    IGetSchemaMapper,
} from "../../../services/middleware/types/singleApplication/getSchemaTypes"

// New interface to represent the complete draft data structure
export interface IDraftSubmissionData {
    installerUserName?: string
    formData?: IFormData
    fixedInputs?: { [key: string]: unknown }
    jurisdiction?: string
    lcts?: string[]
    supplementingInfo?: ISupplementingInfo
    completionStatus?: number
    draftId?: string
    [key: string]: unknown // Allow additional properties
}

export interface IApplicationContextValue {
    stepLabels: string[]
    currentStepIndex: number
    setCurrentStepIndex: Dispatch<SetStateAction<number>>
    applicationForms: IApplicationForms
    isLastStep: boolean
    isAmendmentMode: boolean
    isDraftMode: boolean
    amendmentApplicationId?: string
    applicationStatus?: string // Add this property
    draftId?: string | undefined
    isFirstStep: boolean
    formData: IFormData
    setFormData: Dispatch<SetStateAction<IFormData>>
    // Update draftData to use the new interface type
    draftData: IDraftSubmissionData
    setDraftData: Dispatch<SetStateAction<IDraftSubmissionData>>
    formTitlesIndexMap: IFormTitlesIndexMap
    onClickBackFirstStep: () => void
    fixedInputs: { [x: string]: unknown }
    supplementingInfo: ISupplementingInfo
    setSupplementingInfo: Dispatch<SetStateAction<ISupplementingInfo>>
    displayStepTitles: string[]
    rawApplicationSpec: IApplicationSpec
    lcts: string[]
    jurisdiction: string
    setJurisdiction: Dispatch<SetStateAction<string>>
    draftAutoSave: {
        hasUnsavedChanges: boolean
        saveDraft: () => void
        draftId?: string
    }
    setDraftId: Dispatch<SetStateAction<string | undefined>>
    defaultCompletionStatus: number
    manuallyProvidedMpan: string | null // Add this property
    setManuallyProvidedMpan: Dispatch<SetStateAction<string | null>> // Add this property
}

export const ApplicationContext = createContext({} as IApplicationContextValue)

interface IChildrenProps {
    children: ReactNode
    onClickBackFirstStep: () => void
    lcts: string[]
    defaultFormData?: IFormData
    defaultSupplementingInfo?: ISupplementingInfo
    isAmendmentMode?: boolean
    isDraftMode?: boolean
    amendmentApplicationId?: string
    applicationStatus?: string // Add this property
    draftId?: string // Add missing property here
    defaultCompletionStatus?: number
}
type IApplicationProviderProps = IChildrenProps & IGetSchemaMapper

/**
 * Context provider for the application form. Provides access to data that
 * is globally available to the application form (but nowhere else).
 * If the form is re-rendered, the form data in the context will be reset.
 */
const ApplicationProvider = ({
    rawApplicationSpec,
    applicationStepIndexMap,
    applicationForms,
    formTitlesIndexMap,
    onClickBackFirstStep,
    children,
    fixedInputs,
    lcts,
    isAmendmentMode = false,
    isDraftMode = false,
    defaultFormData = {},
    defaultSupplementingInfo = {},
    amendmentApplicationId = undefined,
    applicationStatus, // Add parameter with default value
    draftId: initialDraftId = undefined, // Add this parameter to receive the prop
    defaultCompletionStatus = 0,
}: IApplicationProviderProps): ReactElement => {
    const stepLabels = Object.values(applicationStepIndexMap)
    const [currentStepIndex, setCurrentStepIndex] = useState(0)
    const [formData, setFormData] = useState(defaultFormData)

    // Initialize draftData with an empty object of the new type
    const [draftData, setDraftData] = useState<IDraftSubmissionData>({})

    const [supplementingInfo, setSupplementingInfo] = useState(
        defaultSupplementingInfo
    )
    const isLastStep = stepLabels.length - 1 === currentStepIndex
    const isFirstStep = currentStepIndex === 0
    const [draftId, setDraftId] = useState<string | undefined>(initialDraftId) // Use the prop to initialize state
    const [jurisdiction, setJurisdiction] = useState<string>("GB")
    const [manuallyProvidedMpan, setManuallyProvidedMpan] = useState<
        string | null
    >(null)
    const displayStepTitles = stepLabels.map(
        (stepLabel) => formTitlesIndexMap[stepLabel].title
    )

    const { postDraftApplication, hasUnsavedChanges, hasBasicDetailsComplete } =
        useSubmitDraft()

    const value = useMemo(
        () => ({
            stepLabels,
            currentStepIndex,
            setCurrentStepIndex,
            applicationForms,
            isLastStep,
            isAmendmentMode,
            isDraftMode,
            isFirstStep,
            formData,
            setFormData,
            draftData,
            setDraftData,
            formTitlesIndexMap,
            onClickBackFirstStep,
            fixedInputs,
            supplementingInfo,
            setSupplementingInfo,
            displayStepTitles,
            rawApplicationSpec,
            lcts,
            jurisdiction,
            setJurisdiction,
            draftAutoSave: {
                hasUnsavedChanges,
                saveDraft: postDraftApplication,
                draftId, // This is redundant but kept for backward compatibility
            },
            draftId,
            setDraftId,
            manuallyProvidedMpan,
            setManuallyProvidedMpan,
            // Conditionally include applicationStatus if it's provided
            ...(isAmendmentMode
                ? { amendmentApplicationId, applicationStatus }
                : {}),
            ...(isDraftMode ? { draftId } : {}),
            defaultCompletionStatus, // Add this property to the value object
        }),
        [
            stepLabels,
            currentStepIndex,
            setCurrentStepIndex,
            applicationForms,
            isLastStep,
            isAmendmentMode,
            isDraftMode,
            amendmentApplicationId,
            applicationStatus, // Add to dependency array
            draftId,
            isFirstStep,
            formData,
            draftData,
            formTitlesIndexMap,
            onClickBackFirstStep,
            fixedInputs,
            supplementingInfo,
            setSupplementingInfo,
            displayStepTitles,
            rawApplicationSpec,
            lcts,
            jurisdiction,
            setJurisdiction,
            hasUnsavedChanges,
            postDraftApplication,
            setDraftId,
            defaultCompletionStatus,
            manuallyProvidedMpan,
        ]
    )

    return (
        <ApplicationContext.Provider value={value}>
            {children}
        </ApplicationContext.Provider>
    )
}

ApplicationProvider.defaultProps = {
    defaultFormData: {},
    defaultSupplementingInfo: {},
    isAmendmentMode: false,
    isDraftMode: false,
    amendmentApplicationId: undefined,
    applicationStatus: undefined,
    draftId: undefined,
    defaultCompletionStatus: 0,
}

export default ApplicationProvider
